import { getPullMeta, logFailure } from '../cache/db'
import {
  COLLECTIONS,
  pullCollection,
  type CollectionKey,
  type PullProgress,
} from './pull'

const COOLDOWN_MS = 5 * 60_000 // 5 min

/**
 * Log an autoSync error that never reached pullCollection's own logging
 * — i.e. pullCollection THREW (db/meta failures) instead of returning
 * PullProgress.error. The returned-error path is logged inside
 * pullCollection itself so every caller (this, Profile's pull-all,
 * per-records pulls, Home) is covered without wiring each one.
 */
function logSyncError(
  collection: CollectionKey,
  targetDid: string,
  targetHandle: string,
  message: string,
): void {
  const status = message.match(/\b([45]\d\d)\b/)
  void logFailure({
    op: 'sync',
    target: `sync:${collection} [repo=${targetDid || targetHandle}]`,
    reason: message,
    httpStatus: status ? Number(status[1]) : undefined,
  })
}

export type SyncStatus = 'idle' | 'running' | 'done' | 'error'

export interface AutoSyncEvent {
  collection: CollectionKey
  pulled: number
  status: SyncStatus
  error?: string
  /** Aggregate counter so the UI can show one total. */
  total: number
}

const PRIORITY: CollectionKey[] = [
  'posts',
  'reposts',
  'likes',
  'follows',
  'blocks',
  'lists',
  'packs',
  'feeds',
]

export interface AutoSyncOpts {
  targetHandle: string
  targetDid: string
  /** Override cooldown — pass 0 to force-sync everything. */
  cooldownMs?: number
  /** When supplied, sync only this single collection. */
  only?: CollectionKey
  onEvent?: (e: AutoSyncEvent) => void
  signal?: AbortSignal
}

/**
 * Automatic incremental sync. Iterates collections in priority order,
 * skips any whose pull_meta.lastPulledAt is within COOLDOWN_MS, runs
 * pullCollection() for the rest in the background. Emits events so a
 * UI can show "syncing posts… +12 → reposts +0 → ✓ up to date".
 *
 * Designed to be called fire-and-forget on profile / home mount so the
 * user never has to click "pull all" — the data just shows up.
 */
export async function autoSync(opts: AutoSyncOpts): Promise<void> {
  const cooldown = opts.cooldownMs ?? COOLDOWN_MS
  const handle = opts.targetHandle.toLowerCase()
  const list: CollectionKey[] = opts.only ? [opts.only] : PRIORITY
  let total = 0
  for (const c of list) {
    if (opts.signal?.aborted) return
    const meta = await getPullMeta(handle, COLLECTIONS[c])
    const stale = !meta || Date.now() - meta.lastPulledAt > cooldown
    if (!stale) {
      opts.onEvent?.({ collection: c, pulled: 0, status: 'idle', total })
      continue
    }
    opts.onEvent?.({ collection: c, pulled: 0, status: 'running', total })
    let lastP: PullProgress | null = null
    try {
      lastP = await pullCollection(c, {
        targetHandle: opts.targetHandle,
        targetDid: opts.targetDid,
        full: false,
        signal: opts.signal,
        onProgress: (p) => {
          opts.onEvent?.({
            collection: c,
            pulled: p.pulled,
            status: 'running',
            total: total + p.pulled,
          })
        },
      })
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      logSyncError(c, opts.targetDid, handle, msg)
      opts.onEvent?.({
        collection: c,
        pulled: 0,
        status: 'error',
        error: msg,
        total,
      })
      continue
    }
    if (lastP?.error) {
      // Already logged inside pullCollection — just surface to the UI.
      opts.onEvent?.({
        collection: c,
        pulled: lastP.pulled,
        status: 'error',
        error: lastP.error,
        total: total + lastP.pulled,
      })
      total += lastP.pulled
      continue
    }
    total += lastP?.pulled ?? 0
    opts.onEvent?.({
      collection: c,
      pulled: lastP?.pulled ?? 0,
      status: 'done',
      total,
    })
  }
}
